// Content for "Astrology & Bhakti" series, Part 2
// Class by HG Balarāma Śakti Dāsa, Bhakti Saṅgha, on SB 5.14.8 (30 October 2021)
//
// This part reproduces Section 3 ("The Third Shower of Nectar") as closely as possible to
// the presentation slides — no meta-commentary about the class, slides, or sourcing is
// shown to the reader. For maintainers: most of this section's objection/response content
// is itself Śrīla Viśvanātha Cakravartī Ṭhākura's commentary (Madhurya-kadambini) on
// SB 10.88.8, labeled "SVCT" on the slides; the lecturer names this work directly in the
// recording.

import type { TopicContent } from '../types';

const content: TopicContent = {
    primarySlokaHeader: 'Śrīmad-Bhāgavatam 10.88.8',
    primarySloka: {
        reference: 'Śrīmad-Bhāgavatam 10.88.8',
        sloka: `yasyāham anugṛhṇāmi
hariṣye tad-dhanaṁ śanaiḥ
tato 'dhanaṁ tyajanty asya
svajanā duḥkha-duḥkhitam`,
        translation:
            'If I especially favor someone (<strong>yasya ahaṁ anugṛhṇāmi</strong>), I gradually deprive him of his wealth (<strong>hariṣye tad-dhanaṁ śanaiḥ</strong>). Then the relatives and friends of such a poverty-stricken man abandon him (<strong>tato \'dhanaṁ tyajanty asya svajanāḥ</strong>). In this way he suffers one distress after another (<strong>duḥkha-duḥkhitam</strong>).',
    },
    blocks: [
        {
            type: 'divider',
            dividerLabel: 'Section 3',
        },
        {
            type: 'heading',
            headingText: 'Section 3 — A Devotee\'s Suffering Is Not Due to His Prārabdha Karma',
        },
        {
            type: 'text',
            content:
                'For the devotee, accumulated sins committed due to previous bad habits have no effect, like the bite of a serpent without its poison fangs. The sickness, lamentation, and other sufferings undergone by devotees are not the reactions to sins in a previous life (prārabdha).',
        },
        {
            type: 'text',
            content:
                'The Lord has also said: <em>nirdhanatva-mahā-rogo mad-anugraha-lakṣaṇam</em> — the awesome affliction known as poverty (<em>nirdhanatva-mahā-rogaḥ</em>) is, in fact, a sign of mercy (<em>mad-anugraha-lakṣaṇam</em>).',
        },
        {
            type: 'text',
            content: 'The Lord Himself has also said, in the verse quoted above:',
        },
        {
            type: 'text',
            content:
                'A man suffers misery because of loss of wealth. He suffers further because of rejection by his relatives because he is poor. This suffering, being given by the Lord, is not the result of karma. <strong>Even the happiness given to the Lord\'s devotees is not the result of karma, but rather an unrequested result of bhakti.</strong>',
        },
        {
            type: 'text',
            content:
                'The happiness and distress which appear to be prārabdha-karma are given by the Lord Himself. The śrutis say, <em>bhavad uttha śubhāśubhayoḥ</em> — when a person realizes You, he no longer cares about his good and bad fortune arising from pious and sinful acts, since it is You alone who control this good and bad fortune (SB 10.87.40).',
        },
        {
            type: 'dialogue',
            objection: 'How can the Lord, so affectionate to His devotee, make him suffer?',
            response:
                '<strong>The father, though affectionate to his sons, gives them the austerity of studying by taking away their sources of enjoyment. The father knows that this action is a sign of his affection, though the son does not appreciate it at that time.</strong>',
        },
        {
            type: 'dialogue',
            objection:
                'As the experience of happiness or suffering coming either from karma or directly from the Lord is similar, what distinguishes them?',
            response:
                'Even though experiencing happiness or suffering arising from karma — therefore finishing with the effects — a person still maintains the seed, or desire, the cause which will produce further happiness and suffering. The cause is desire for enjoyment. That seed continues, and produces more suffering. According to the degree of karma or sin, the person experiences a certain degree of happiness or suffering, even to the extent of falling to hell. Thus there are three qualities of karmic suffering.',
        },
        {
            type: 'table',
            table: {
                headers: ['The Three Qualities of Karmic Suffering', 'The Three Qualities of Suffering Given by the Lord'],
                rows: [
                    [
                        'Even though the experience of happiness and distress destroys the karma, the desire is not gone.',
                        'The suffering induced by the Lord Himself is by His desire alone (not by the jīva\'s desires).',
                    ],
                    [
                        'That desire leads to more actions, which lead to further happiness and distress.',
                        'That is the seed, and the seed lasts only as long as necessary for the Lord\'s purpose, and no longer.',
                    ],
                    [
                        'The distress can many times be extreme, even hellish, suffering.',
                        'Because of the affection of the Lord for His devotee, the Lord does not give them extreme suffering.',
                    ],
                ],
            },
        },
        {
            type: 'sloka',
            sloka: {
                reference: 'Śrīmad-Bhāgavatam 6.3.29',
                sloka: `jihvā na vakti bhagavad-guṇa-nāmadheyaṁ
cetaś ca na smarati tac-caraṇāravindam
kṛṣṇāya no namati yac-chira ekadāpi
tān ānayadhvam asato 'kṛta-viṣṇu-kṛtyān`,
                translation:
                    'Bring to me the non-devotees (<strong>tān asato ānayadhvam</strong>) who have never served Viṣṇu (<strong>akṛta-viṣṇu-kṛtyān</strong>), whose heads (<strong>yad-śiraḥ</strong>) have never once bowed (<strong>ekadāpi no namati</strong>) to Kṛṣṇa (<strong>kṛṣṇāya</strong>), whose tongues have never pronounced (<strong>jihvā na vakti</strong>) the name and qualities of the Lord (<strong>bhagavad-guṇa-nāmadheyaṁ</strong>), whose minds have never remembered (<strong>cetaś na smarati</strong>) his lotus feet (<strong>tac-caraṇāravindam</strong>).',
            },
        },
        {
            type: 'text',
            content:
                'From this it is understood that for the devotee suffering because of the Lord, there is no fall to hell. Because of the affection of the Lord for his devotee, the Lord does not give them extreme suffering. The suffering arising from karma and the Lord are like the suffering arising from receiving a beating from an enemy and one\'s mother. One is like poison and the other is like nectar.',
        },
        {
            type: 'dialogue',
            objection: 'But can\'t the Lord fulfill His purpose without giving suffering to His devotees?',
            response:
                'It will not be fulfilled. He gives suffering to His dearmost, which ultimately results in happiness, like applying stinging ointment to the eye.',
        },
        {
            type: 'table',
            table: {
                headers: ['#', 'Three Purposes for the Lord to Give Suffering to His Devotees'],
                rows: [
                    ['1', 'In order to preserve the secret of bhakti.'],
                    ['2', 'To avoid uprooting the opinions of the speculators and Smārtas.'],
                    ['3', 'To increase the longing of His devotees.'],
                ],
            },
        },
        {
            type: 'text',
            content:
                'Moreover, if the Lord made the devotees always happy, there would be no avatāras such as Kṛṣṇa and Rāma, since they come to protect the devotees and destroy the demons. And if the avatāras did not come, how could there be the playing of the devotees in the sweet ocean of pastimes, such as rāsa-līlā?',
        },
        {
            type: 'dialogue',
            objection: 'But is it a fault if the Lord comes without having to deliver the devotee from suffering? Can\'t He come without the devotee suffering at all?',
            response:
                'O brother, you are indeed not familiar with rasa. Just listen. The sunrise appears splendid because of the darkness. In the summer, coolness is pleasant, and in the cold season, warmth is pleasant. In the darkness, a lamp appears splendid rather than in the light. Food tastes delicious when there is suffering from hunger. There is no need to elaborate more.',
        },
        {
            type: 'text',
            content:
                '<strong>The truth is that the Supreme Lord, the greatest benefactor of His devotees, purposely inflicts suffering on His devotees just to increase their humility and yearning for Him. Thus the devotee\'s woes are not because of fruitive reactions, nor are they reactions of prārabdha sins.</strong>',
        },
        {
            type: 'divider',
        },
        {
            type: 'text',
            content:
                'The next part in this series continues with Section 4: The role of astrology in a devotee\'s life.',
        },
    ],
};

export default content;
